import type { models } from "jiffy-api";
import type {
  PlacementData,
  PlacementInfo,
  DesignProjectResponse,
  ArtworkFile,
  DesignLabFormType
} from "@design-lab/shared/types";

// Helper function to create placement payload
export function createPlacementPayload(
  placement: PlacementData,
  info: PlacementInfo,
  position: string
) {
  return {
    width: placement.size.width,
    height: placement.size.height,
    position,
    enhanceImage: info.enhancementTier !== "print_original",
    enhancementRequestNote: info.enhancementRequestNote || "",
    enhancementTier: info.enhancementTier,
    removeBackground: info.removeBackground,
    editImageManual: info.editImageManual || false,
    dtfArtworkId: placement.artworkId,
    customNeckDistances: null
  };
}

// Helper function to get active front placement
export function getActiveFrontPlacement(artwork: DesignLabFormType["artwork"]) {
  const isLeftChest = artwork.selectedFrontPlacement === "frontLeftChest";
  const placement = isLeftChest ? artwork.frontLeftChest : artwork.front;
  const position = isLeftChest ? "front_left_chest" : "front";
  return { placement, position };
}

// Helper function to determine enhancement tier
export function getEnhancementTier(
  enhanceImage: boolean,
  enhancementTier?: string
): models.cart.madeAdd.EnhancementTier {
  if (!enhanceImage) return "print_original";
  return (enhancementTier || "full_enhancements") as models.cart.madeAdd.EnhancementTier;
}

// Helper function to create placement form data
export function createPlacementFormData(placement: DesignProjectResponse["placements"][string]): {
  placementData: PlacementData;
  info: PlacementInfo;
  artworkFile?: ArtworkFile | undefined;
} {
  const { dtfArtwork } = placement;
  const url = dtfArtwork["url"] as string | undefined;

  return {
    placementData: {
      artworkId: String(placement.dtfArtwork.id),
      size: {
        width: Number(placement.width),
        height: Number(placement.height)
      }
    },
    info: {
      enhancementTier: getEnhancementTier(
        placement.enhanceImage,
        placement.dtfArtwork.enhancementTier
      ),
      removeBackground: placement.removeBackground,
      enhancementRequestNote: placement.enhancementRequestNote,
      editImageManual: placement.editImageManual || false
    },
    artworkFile: url
      ? {
          id: String(placement.dtfArtwork.id),
          url
        }
      : undefined
  };
}
